import {
  Button,
  Flex,
  HStack,
  IconButton,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import {
  PageHeader,
  PageToolbar,
  TeamMenu,
  Loader,
  useToastHook,
  DateFilter,
  DownloadButton,
  TeamTable,
  ContributorSettingsIcon,
  ConfigureColumns,
  ContributorTableCompareIcon,
  useTeamTableStore,
  ApiResponse,
  PageDetailsDto,
  MetricTooltip,
  TeamCompareIcon,
} from '@devd-client/devd/components';
import { useNavigate } from 'react-router-dom';
import { AiOutlinePlus } from 'react-icons/ai';
import { useEffect, useState } from 'react';
import {
  AuthActionTypes,
  getUser,
  getOrgId,
  getToken,
} from '@devd-client/devd/auth';
import { apiClient } from '@devd-client/api';
import { useSaveTeamMetricsMutation } from '../api';
import { useTeamTable } from '../hooks/useTeamTable';
import { MetricState } from '../utils/TeamTableTypes';

interface TeamDetailsPageProps {
  teamsData: ApiResponse<PageDetailsDto> | undefined;
}

export const TeamDetailsPage: React.FC<TeamDetailsPageProps> = ({
  teamsData,
}) => {
  const {
    selected,
    selectedTeam,
    setSelectedTeam,
    appState,
    dispatch,
    teamLength,
    selectedMetrics,
    setSelectedMetrics,
    selectedPermanentColumns,
    setSelectedPermanentColumns,
    compareTo,
    setCompareTo,
    orderBy,
    setOrderBy,
    orderColumn,
    setOrderColumn,
    setOrderByUrl,
    processedMetrics,
  } = useTeamTable();

  const navigate = useNavigate();
  const [newToast] = useToastHook();
  const helpDocLink = teamsData?.dto?.helpDoc;
  const plan = appState?.user?.dto?.plan;
  const configureMetricsModal = useDisclosure();
  const { setSelectedTeams } = useTeamTableStore();

  const { mutate: saveTeamMetrics, isLoading: saveTeamMetricsLoading } =
    useSaveTeamMetricsMutation();

  useEffect(() => {
    return () => {
      setSelectedTeams([]);
    };
  }, []);

  const [pending, setPending] = useState(false);
  const downloadCSV = async () => {
    setPending(true);

    // Get selected columns from ConfigureColumns metric list
    const selectedColumns = selectedMetrics.map(
      (metric: MetricState) => metric.metricKey
    );

    const downloadParams = {
      filter: {
        orgId: getOrgId(),
        startDate: selected.startDate,
        endDate: selected.endDate,
        teamId: selectedTeam.value === '' ? 'all' : selectedTeam.value || 'all',
      },
      selectedColumns: selectedColumns,
    };

    apiClient(`/v1/team/org/${getOrgId()}/csv`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        authorization: `bearer ${getToken()}`,
      },
      body: JSON.stringify(downloadParams),
    })
      .then((res: any) => {
        setPending(false);

        newToast({
          message: res.dto || 'We will send you the download link via email',
          status: 'success',
        });
      })
      .catch((err) => {
        setPending(false);
        newToast({
          message:
            err.message ||
            'There is some error in downloading data. Please try again or contact support at support@devdynamics.ai',
          status: 'error',
        });
      });
  };

  return (
    <Flex w="full" direction="column" gap={4}>
      <PageHeader
        headingText="Teams"
        subHeadingText={teamsData?.dto?.title}
        helpDocLink={helpDocLink ?? ''}
        component={
          <Flex gap={4}>
            <Button
              variant="custom-outline"
              size="sm"
              color="text.primary"
              borderColor="text.primary"
              onClick={() => {
                navigate('/compare-teams');
              }}
              leftIcon={<TeamCompareIcon />}
            >
              Compare Teams
            </Button>
            <Button
              variant="custom-outline"
              size="sm"
              leftIcon={<AiOutlinePlus />}
              onClick={() => {
                if (
                  appState?.user?.dto?.teamAllowed &&
                  (plan === 'Free' || plan === 'Trial') &&
                  teamLength >= appState?.user?.dto?.teamAllowed
                ) {
                  newToast({
                    message:
                      'Upgrade your plan to add more teams, or contact Devdynamics support',
                    status: 'error',
                  });
                } else {
                  navigate('/settings/teams/add-team');
                }
              }}
            >
              New Team
            </Button>
          </Flex>
        }
      />

      <Flex justify="space-between" align="center" width="100%">
        <TeamMenu
          selectedTeam={selectedTeam}
          handleSelectedTeam={setSelectedTeam}
          AuthActionTypes={AuthActionTypes}
          getUser={getUser}
          appState={appState}
          dispatch={dispatch}
        />

        <Flex gap={2} mt="auto">
          <PageToolbar
            rightComp={
              <HStack spacing={2}>
                <Tooltip hasArrow label="Compare to Past Period">
                  <IconButton
                    bg="transparent"
                    border="1px solid #E0E0E0"
                    borderRadius="4px"
                    aria-label="circle"
                    size="sm"
                    onClick={() => setCompareTo('pastPeriod')}
                    icon={<ContributorTableCompareIcon />}
                  />
                </Tooltip>
                <Button
                  bg="transparent"
                  border="1px solid #E0E0E0"
                  borderRadius="4px"
                  color="text.secondary2"
                  size="sm"
                  fontWeight="normal"
                  onClick={configureMetricsModal.onOpen}
                  leftIcon={<ContributorSettingsIcon />}
                >
                  Configure Columns
                </Button>
                <DateFilter
                  getUser={getUser}
                  appState={appState}
                  dispatch={dispatch}
                  AuthActionTypes={AuthActionTypes}
                />
              </HStack>
            }
          />

          <DownloadButton downloadCSV={downloadCSV} />
        </Flex>
      </Flex>

      {configureMetricsModal.isOpen && (
        <ConfigureColumns
          {...configureMetricsModal}
          componentOrigin="team-table"
          saveColumns={saveTeamMetrics}
          saveColumnsIsLoading={saveTeamMetricsLoading}
          metricList={[
            ...(processedMetrics || []).map((metric: MetricTooltip) => {
              return {
                metricName: metric?.title ?? '',
                metricKey: metric?.metric,
                tabs: metric?.metric === 'CUSTOM' ? ['Custom'] : metric?.tags,
                tooltip: metric?.info,
                id: metric?.id ?? metric?.metric,
              };
            }),
            {
              metricName: 'Git Activity',
              metricKey: 'GIT_ACTIVITY',
              tabs: ['Productivity'],
              tooltip: '',
              id: 'GIT_ACTIVITY',
            },
            {
              metricName: 'Issue Activity',
              metricKey: 'ISSUE_ACTIVITY',
              tabs: ['Productivity'],
              tooltip: '',
              id: 'ISSUE_ACTIVITY',
            },
            {
              metricName: 'DORA Activity',
              metricKey: 'DORA_ACTIVITY',
              tabs: ['DORA'],
              tooltip: '',
              id: 'DORA_ACTIVITY',
            },
            {
              metricName: 'AI Adoption',
              metricKey: 'AI_ADOPTION',
              tabs: [''],
              tooltip: '',
              id: 'AI_ADOPTION',
            },
            {
              metricName: 'Goals',
              metricKey: 'GOALS',
              tabs: ['Goals'],
              tooltip:
                'Goals are the main objectives that teams aim to achieve. They are typically set for a specific period and are used to track progress and measure success.',
              id: 'GOALS',
            },
            {
              metricName: 'Key Results',
              metricKey: 'KEY_RESULTS',
              tabs: ['Goals'],
              tooltip:
                "Measures how often a Key Result's target was met within its timeframe. E.g., if a KR met its target in 4 out of 5 months, its success is 80%. This shows the consistency of achieving a specific outcome.",
              id: 'KEY_RESULTS',
            },
          ]}
          selectedMetrics={selectedMetrics}
          setSelectedMetrics={setSelectedMetrics}
          selectedPermanentColumns={selectedPermanentColumns}
          setSelectedPermanentColumns={setSelectedPermanentColumns}
          permanentColumns={['Teams', 'Members']}
          defaultMetrics={[
            'CONTRIBUTOR_COUNT',
            'GIT_ACTIVITY',
            'ISSUE_ACTIVITY',
            'DORA_ACTIVITY',
            'AI_ADOPTION',
          ]}
        />
      )}

      <TeamTable
        teamId={selectedTeam.value}
        selectedMetrics={selectedMetrics}
        hiddenColumns={['Members'].filter(
          (item) => !selectedPermanentColumns.includes(item)
        )}
        compareTo={compareTo}
        metricList={processedMetrics}
        orderBy={orderBy}
        setOrderBy={setOrderBy}
        orderColumn={orderColumn}
        setOrderColumn={setOrderColumn}
        setOrderByUrl={setOrderByUrl}
      />

      {pending && <Loader />}
    </Flex>
  );
};
export default TeamDetailsPage;
